import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen,
  PlayCircle,
  Newspaper,
  PenLine,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Award,
  BarChart3,
  Flame,
  Clock,
  Lock,
  GraduationCap,
  Brain,
  Target,
  ChevronRight,
  Compass,
} from "lucide-react";

interface CourseProgress {
  courseId: string;
  courseTitle: string;
  courseSlug: string;
  totalModules: number;
  completedModules: number;
}

interface RecentLesson {
  courseSlug: string;
  moduleSlug: string;
  courseTitle: string;
  moduleTitle: string;
  moduleOrder: number;
}

interface FeaturedCourse {
  id: string;
  title: string;
  slug: string;
  subtitle: string | null;
  cover_image_url: string | null;
  difficulty: string | null;
  estimated_hours: number | null;
  module_count: number;
  required_tier: string;
  is_free_preview: boolean;
}

interface JournalStats {
  count: number;
  winRate: number | null;
  avgRR: number | null;
}

const fadeInUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.08 } },
};

const Dashboard = () => {
  const { user, hasActiveSubscription } = useAuth();
  const [greeting, setGreeting] = useState("Good day");
  const [progress, setProgress] = useState<CourseProgress[]>([]);
  const [recentLesson, setRecentLesson] = useState<RecentLesson | null>(null);
  const [journalStats, setJournalStats] = useState<JournalStats>({ count: 0, winRate: null, avgRR: null });
  const [featuredCourses, setFeaturedCourses] = useState<FeaturedCourse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good morning");
    else if (hour < 17) setGreeting("Good afternoon");
    else setGreeting("Good evening");
  }, []);

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      const { data: courses } = await supabase
        .from("courses")
        .select("id, title, slug")
        .eq("is_published", true)
        .order("sort_order", { ascending: true });

      if (courses) {
        const courseIds = courses.map((c) => c.id);
        const { data: modules } = await supabase
          .from("modules")
          .select("course_id, id")
          .eq("is_published", true)
          .in("course_id", courseIds);

        const { data: moduleProgress } = await supabase
          .from("module_progress")
          .select("module_id, quiz_passed_at")
          .eq("user_id", user.id);

        const passedModuleIds = new Set(
          moduleProgress?.filter((p) => p.quiz_passed_at).map((p) => p.module_id) ?? []
        );

        const progressData: CourseProgress[] = courses.map((course) => {
          const courseModules = modules?.filter((m) => m.course_id === course.id) ?? [];
          const completed = courseModules.filter((m) => passedModuleIds.has(m.id)).length;
          return {
            courseId: course.id,
            courseTitle: course.title,
            courseSlug: course.slug,
            totalModules: courseModules.length,
            completedModules: completed,
          };
        });

        setProgress(progressData);

        const { data: recentProgress } = await supabase
          .from("module_progress")
          .select("module_id, updated_at")
          .eq("user_id", user.id)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (recentProgress?.module_id) {
          const { data: mod } = await supabase
            .from("modules")
            .select("slug, title, sort_order, course_id")
            .eq("id", recentProgress.module_id)
            .maybeSingle();

          if (mod) {
            const course = courses.find((c) => c.id === mod.course_id);
            if (course) {
              setRecentLesson({
                courseSlug: course.slug,
                moduleSlug: mod.slug,
                courseTitle: course.title,
                moduleTitle: mod.title,
                moduleOrder: mod.sort_order + 1,
              });
            }
          }
        }
      }

      // Featured courses for browseable sections
      const { data: featured } = await supabase
        .from("courses")
        .select("id, title, slug, subtitle, cover_image_url, difficulty, estimated_hours, required_tier, is_free_preview")
        .eq("is_published", true)
        .order("sort_order", { ascending: true })
        .limit(12);

      if (featured) {
        const { data: moduleCounts } = await supabase
          .from("modules")
          .select("course_id")
          .eq("is_published", true)
          .in("course_id", featured.map((c) => c.id));

        const counts = new Map<string, number>();
        moduleCounts?.forEach((m) => {
          counts.set(m.course_id, (counts.get(m.course_id) || 0) + 1);
        });

        setFeaturedCourses(
          featured.map((c) => ({
            ...c,
            module_count: counts.get(c.id) || 0,
            required_tier: c.required_tier || "free",
          }))
        );
      }

      // Real journal stats from trade_entries
      const { count } = await supabase
        .from("trade_entries")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      const journalCount = count ?? 0;
      let winRate: number | null = null;
      let avgRR: number | null = null;

      if (journalCount > 0) {
        const { data: trades } = await supabase
          .from("trade_entries")
          .select("status, risk_reward")
          .eq("user_id", user.id)
          .in("status", ["won", "lost"]);

        if (trades && trades.length > 0) {
          const wins = trades.filter((t) => t.status === "won").length;
          winRate = Math.round((wins / trades.length) * 100);
          const rrValues = trades.filter((t) => t.risk_reward && t.risk_reward > 0).map((t) => t.risk_reward!);
          avgRR = rrValues.length > 0 ? parseFloat((rrValues.reduce((a, b) => a + b, 0) / rrValues.length).toFixed(1)) : null;
        }
      }

      setJournalStats({ count: journalCount, winRate, avgRR });
      setLoading(false);
    };

    load();
  }, [user]);

  const totalModules = progress.reduce((sum, c) => sum + c.totalModules, 0);
  const completedModules = progress.reduce((sum, c) => sum + c.completedModules, 0);
  const overallPercent = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

  const displayName = user?.user_metadata?.full_name?.split(" ")[0] || user?.email?.split("@")[0];

  const beginnerCourses = featuredCourses.filter((c) => c.difficulty === "beginner");
  const psychologyCourses = featuredCourses.filter((c) => c.title.toLowerCase().includes("psychology") || c.subtitle?.toLowerCase().includes("psychology"));
  const marketCourses = featuredCourses.filter((c) => c.title.toLowerCase().includes("market") || c.title.toLowerCase().includes("breakdown"));
  const topPicks = featuredCourses.slice(0, 6);

  /* ------------------------------------------------------------------ */
  //  Course Card (for horizontal scroll sections)
  /* ------------------------------------------------------------------ */
  const CourseCard = ({ course }: { course: FeaturedCourse }) => {
    const diffColor =
      course.difficulty === "beginner"
        ? "bg-emerald-500/10 text-emerald-600"
        : course.difficulty === "intermediate"
        ? "bg-amber-500/10 text-amber-600"
        : "bg-rose-500/10 text-rose-600";
    return (
      <Link to={`/course/${course.slug}`} className="shrink-0 w-52 snap-start">
        <div className="group rounded-2xl overflow-hidden border border-border/40 bg-card/60 hover:bg-card transition-all hover:border-primary/20 hover:shadow-sm">
          <div className="aspect-[16/10] relative overflow-hidden">
            {course.cover_image_url ? (
              <img src={course.cover_image_url} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                <GraduationCap className="h-8 w-8 text-primary/50" />
              </div>
            )}
            {!course.is_free_preview && (
              <div className="absolute top-2 right-2 h-6 w-6 rounded-full bg-black/50 flex items-center justify-center">
                <Lock className="h-3 w-3 text-white" />
              </div>
            )}
          </div>
          <div className="p-3.5 space-y-2">
            <h4 className="font-display font-medium text-sm leading-snug line-clamp-2">{course.title}</h4>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              {course.difficulty && <span className={`px-1.5 py-0.5 rounded-md font-medium ${diffColor}`}>{course.difficulty}</span>}
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{course.estimated_hours ?? "?"}h</span>
            </div>
          </div>
        </div>
      </Link>
    );
  };

  const SectionHeader = ({ title, icon: Icon, action }: { title: string; icon: typeof BookOpen; action?: React.ReactNode }) => (
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-display font-semibold text-[15px] flex items-center gap-2">
        <Icon className="h-[18px] w-[18px] text-primary" />
        {title}
      </h3>
      {action}
    </div>
  );

  return (
    <motion.div className="space-y-8" variants={staggerContainer} initial="initial" animate="animate">
      {/* Welcome */}
      <motion.div variants={fadeInUp}>
        <Card className="p-5 md:p-6 rounded-2xl border-border/60">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Compass className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">{greeting}, {displayName}</p>
                <h1 className="text-lg md:text-xl font-display font-semibold tracking-tight mt-0.5">
                  Welcome to Limitless Traders
                </h1>
              </div>
            </div>
            <div className="md:ml-auto">
              {recentLesson ? (
                <Link to={`/lesson/${recentLesson.courseSlug}/${recentLesson.moduleSlug}`}>
                  <Button className="gap-2 rounded-xl h-10 px-4">
                    <PlayCircle className="h-4 w-4" />
                    Continue Learning
                  </Button>
                </Link>
              ) : (
                <Link to="/learn">
                  <Button className="gap-2 rounded-xl h-10 px-4">
                    <BookOpen className="h-4 w-4" />
                    Start Learning
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Stats Row — real data only */}
      <motion.div variants={fadeInUp}>
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4 rounded-2xl border-border/50 bg-card/60">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <Award className="h-3.5 w-3.5 text-primary" />
              </div>
              <span className="text-xs text-muted-foreground font-medium">Progress</span>
            </div>
            <div className="text-xl font-display font-semibold">{overallPercent}%</div>
          </Card>
          <Card className="p-4 rounded-2xl border-border/50 bg-card/60">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-7 w-7 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Flame className="h-3.5 w-3.5 text-emerald-500" />
              </div>
              <span className="text-xs text-muted-foreground font-medium">Journal</span>
            </div>
            <div className="text-xl font-display font-semibold">{journalStats.count}</div>
          </Card>
          <Card className="p-4 rounded-2xl border-border/50 bg-card/60">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-7 w-7 rounded-lg bg-secondary flex items-center justify-center">
                <GraduationCap className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <span className="text-xs text-muted-foreground font-medium">Courses</span>
            </div>
            <div className="text-xl font-display font-semibold">{featuredCourses.length}</div>
          </Card>
        </div>
      </motion.div>

      {/* Featured Courses — horizontal scroll */}
      {topPicks.length > 0 && (
        <motion.div variants={fadeInUp} className="space-y-1">
          <SectionHeader
            title="Featured Courses"
            icon={Sparkles}
            action={
              <Link to="/learn" className="text-xs text-primary font-medium flex items-center gap-1 hover:underline">
                View all <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            }
          />
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x scrollbar-hide">
            {loading
              ? [1, 2, 3, 4].map((i) => <Skeleton key={i} className="shrink-0 w-52 h-36 rounded-2xl" />)
              : topPicks.map((course) => <CourseCard key={course.id} course={course} />)}
          </div>
        </motion.div>
      )}

      {/* Beginner Path */}
      {beginnerCourses.length > 0 && (
        <motion.div variants={fadeInUp} className="space-y-1">
          <SectionHeader title="Beginner Path" icon={Target} />
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x scrollbar-hide">
            {beginnerCourses.map((course) => <CourseCard key={course.id} course={course} />)}
          </div>
        </motion.div>
      )}

      {/* Trading Psychology */}
      {psychologyCourses.length > 0 && (
        <motion.div variants={fadeInUp} className="space-y-1">
          <SectionHeader title="Trading Psychology" icon={Brain} />
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x scrollbar-hide">
            {psychologyCourses.map((course) => <CourseCard key={course.id} course={course} />)}
          </div>
        </motion.div>
      )}

      {/* Market Breakdown Series */}
      {marketCourses.length > 0 && (
        <motion.div variants={fadeInUp} className="space-y-1">
          <SectionHeader title="Market Breakdown Series" icon={Newspaper} />
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x scrollbar-hide">
            {marketCourses.map((course) => <CourseCard key={course.id} course={course} />)}
          </div>
        </motion.div>
      )}

      {/* Two-column layout for desktop */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Learning Progress */}
        <motion.div variants={fadeInUp}>
          <Card className="p-5 space-y-5 rounded-2xl border-border/50">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-semibold text-[15px] flex items-center gap-2">
                <BarChart3 className="h-[18px] w-[18px] text-primary" />
                Learning Progress
              </h2>
              <span className="text-sm font-display font-semibold text-primary">{overallPercent}%</span>
            </div>

            {loading ? (
              <Skeleton className="h-2 w-full rounded-full" />
            ) : (
              <div className="space-y-2">
                <Progress value={overallPercent} className="h-2 bg-secondary/60" />
              </div>
            )}

            <div className="space-y-3">
              {loading
                ? [1, 2].map((i) => <Skeleton key={i} className="h-12 rounded-xl" />)
                : progress.slice(0, 3).map((course) => {
                    const pct =
                      course.totalModules > 0
                        ? Math.round((course.completedModules / course.totalModules) * 100)
                        : 0;
                    return (
                      <div key={course.courseId} className="flex items-center justify-between py-1">
                        <span className="text-sm font-medium truncate max-w-[55%]">
                          {course.courseTitle}
                        </span>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-secondary/60 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary/80 rounded-full"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground w-12 text-right">
                            {course.completedModules}/{course.totalModules}
                          </span>
                        </div>
                      </div>
                    );
                  })}
            </div>

            <Link to="/learn">
              <Button variant="ghost" size="sm" className="w-full gap-1 text-muted-foreground hover:text-foreground rounded-xl h-10">
                View all courses <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </Card>
        </motion.div>

        {/* Continue Learning */}
        <motion.div variants={fadeInUp}>
          <Card className="p-5 space-y-4 rounded-2xl border-border/50">
            <h2 className="font-display font-semibold text-[15px] flex items-center gap-2">
              <Clock className="h-[18px] w-[18px] text-primary" />
              Continue Learning
            </h2>

            {loading ? (
              <Skeleton className="h-24 rounded-xl" />
            ) : recentLesson ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border border-border/60 bg-secondary/40 text-muted-foreground">
                    {recentLesson.courseTitle}
                  </span>
                  <h3 className="font-display font-semibold text-lg leading-tight">{recentLesson.moduleTitle}</h3>
                  <p className="text-sm text-muted-foreground">Module {recentLesson.moduleOrder}</p>
                </div>
                <Link to={`/lesson/${recentLesson.courseSlug}/${recentLesson.moduleSlug}`}>
                  <Button size="sm" className="gap-2 rounded-xl h-10 px-4 bg-primary text-primary-foreground hover:bg-primary/90">
                    <PlayCircle className="h-4 w-4" /> Resume
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <h3 className="font-display font-semibold text-[15px]">Start Here: Forex Foundations</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                    Build a strong foundation and learn how professional traders think about markets, risk, and discipline.
                  </p>
                </div>
                <Link to="/learn">
                  <Button size="sm" className="rounded-xl h-10 px-4 gap-1.5">
                    <BookOpen className="h-4 w-4" /> Start Learning
                  </Button>
                </Link>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Market Breakdown */}
        <motion.div variants={fadeInUp}>
          <Card className="p-5 space-y-4 rounded-2xl border-border/50">
            <h2 className="font-display font-semibold text-[15px] flex items-center gap-2">
              <Newspaper className="h-[18px] w-[18px] text-primary" />
              Daily Market Breakdown
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-secondary/80 flex items-center justify-center shrink-0 mt-0.5">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">EUR/USD Analysis</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Price reacting around a key support zone. Learn how professionals assess whether structure is holding or breaking.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-secondary/80 flex items-center justify-center shrink-0 mt-0.5">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">Gold (XAU/USD)</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Consolidating near historical highs. A lesson in patience and waiting for clear structure before acting.
                  </p>
                </div>
              </div>
            </div>
            <Link to="/market-breakdown">
              <Button variant="outline" size="sm" className="gap-2 rounded-xl h-9 border-border/60 hover:bg-secondary/40">
                View Full Breakdown <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </Card>
        </motion.div>

        {/* Journal Insights */}
        <motion.div variants={fadeInUp}>
          <Card className="p-5 space-y-4 rounded-2xl border-border/50">
            <h2 className="font-display font-semibold text-[15px] flex items-center gap-2">
              <PenLine className="h-[18px] w-[18px] text-primary" />
              Journal Insights
            </h2>
            {journalStats.count > 0 ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl bg-secondary/40">
                    <div className="text-lg font-display font-semibold text-emerald-500">{journalStats.winRate ?? "—"}{journalStats.winRate !== null ? "%" : ""}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Win Rate</div>
                  </div>
                  <div className="p-3 rounded-xl bg-secondary/40">
                    <div className="text-lg font-display font-semibold text-primary">{journalStats.avgRR ?? "—"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Avg R:R</div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  You've logged {journalStats.count} trade entries. Keep building discipline through reflection.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Start journaling your trades to unlock performance insights. Track emotions, review decisions, and build self-awareness.
                </p>
                <Link to="/journal">
                  <Button size="sm" className="gap-2 rounded-xl h-10 px-4 bg-primary text-primary-foreground hover:bg-primary/90">
                    <PenLine className="h-4 w-4" /> Log First Trade
                  </Button>
                </Link>
              </div>
            )}
            {journalStats.count > 0 && (
              <Link to="/journal">
                <Button variant="outline" size="sm" className="gap-2 rounded-xl h-9 border-border/60 hover:bg-secondary/40">
                  <PenLine className="h-3.5 w-3.5" /> Open Journal
                </Button>
              </Link>
            )}
          </Card>
        </motion.div>
      </div>

      {/* Membership CTA */}
      {!hasActiveSubscription && (
        <motion.div variants={fadeInUp}>
          <Card className="p-5 rounded-2xl border-border/60">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-[15px]">Unlock Limitless Pro</h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed max-w-md">
                    AI Coaching, advanced learning paths, premium market breakdowns, and professional trader frameworks.
                  </p>
                </div>
              </div>
              <Link to="/membership">
                <Button className="rounded-xl h-11 px-6 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm shadow-primary/10 shrink-0">
                  View Plans
                </Button>
              </Link>
            </div>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Dashboard;
